import { useId } from "react";
import type { ReactElement } from "react";
import { applyTheme } from "@kundeninfo/ui";
import type { ColorScheme, Density } from "@kundeninfo/ui";
import "./Einstellungen.css";

export type Preferences = {
  colorScheme: ColorScheme;
  density: Density;
  rememberPanels: boolean;
};

export type EinstellungenProps = Preferences & {
  onChange: (p: Preferences) => void;
};

const FARBSCHEMATA: { value: ColorScheme; label: string }[] = [
  { value: "SYSTEM", label: "System" },
  { value: "LIGHT", label: "Hell" },
  { value: "DARK", label: "Dunkel" },
];

const DICHTEN: { value: Density; label: string }[] = [
  { value: "COMPACT", label: "Kompakt" },
  { value: "COMFORTABLE", label: "Komfortabel" },
];

/**
 * Reine Darstellungslogik: Jede Auswahl wendet applyTheme sofort an — die Seite blitzt
 * beim nächsten Aufruf nicht kurz hell auf — und meldet den vollständigen neuen Stand
 * nach außen. Das nebenläufige Speichern am Server (ohne Rückrollen bei einem Fehler)
 * ist Sache des Aufrufers; siehe App.tsx.
 *
 * Die Schrift ist bewusst keine Option hier — sie ist eine Systemeinstellung der
 * Administration, keine Benutzerwahl.
 */
export function Einstellungen(p: EinstellungenProps): ReactElement {
  const schemaGruppe = useId();
  const dichteGruppe = useId();

  function waehleSchema(colorScheme: ColorScheme): void {
    applyTheme(colorScheme, p.density);
    p.onChange({ colorScheme, density: p.density, rememberPanels: p.rememberPanels });
  }

  function waehleDichte(density: Density): void {
    applyTheme(p.colorScheme, density);
    p.onChange({ colorScheme: p.colorScheme, density, rememberPanels: p.rememberPanels });
  }

  function wechsleRandbereiche(rememberPanels: boolean): void {
    p.onChange({ colorScheme: p.colorScheme, density: p.density, rememberPanels });
  }

  return (
    <div className="es">
      <fieldset className="es__gruppe">
        <legend>Farbschema</legend>
        <div className="es__optionen" role="radiogroup" aria-label="Farbschema">
          {FARBSCHEMATA.map((f) => (
            <label key={f.value} className="es__option">
              <input
                type="radio"
                name={schemaGruppe}
                value={f.value}
                checked={p.colorScheme === f.value}
                onChange={() => waehleSchema(f.value)}
              />
              {f.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="es__gruppe">
        <legend>Dichte</legend>
        <div className="es__optionen" role="radiogroup" aria-label="Dichte">
          {DICHTEN.map((d) => (
            <label key={d.value} className="es__option">
              <input
                type="radio"
                name={dichteGruppe}
                value={d.value}
                checked={p.density === d.value}
                onChange={() => waehleDichte(d.value)}
              />
              {d.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="es__gruppe">
        <legend>Randbereiche</legend>
        <label className="es__option">
          <input
            type="checkbox"
            checked={p.rememberPanels}
            onChange={(e) => wechsleRandbereiche(e.target.checked)}
          />
          Navigation und Kontext beim nächsten Besuch wie zuletzt anzeigen
        </label>
      </fieldset>
    </div>
  );
}
